/**
 * Criterios para exportar Excel mayorista «limpio» (reimportación) sin depender de SAP services.
 */

/**
 * Indica si la fila tiene faltante de stock. Usa `stockInsufficient` calculado contra la **demanda total del
 * SKU** (`enrichBulkPreviewRowsWithSapItems`); si la fila no lo trae (origen antiguo), compara la línea sola.
 *
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function bulkPreviewRowHasStockShortage(row) {
  if (row == null || typeof row !== 'object') return false;
  if (typeof row.stockInsufficient === 'boolean') return row.stockInsufficient;
  const cant = row.cantOc;
  const stk = row.stockDispSap;
  return (
    cant != null &&
    stk != null &&
    Number.isFinite(Number(cant)) &&
    Number.isFinite(Number(stk)) &&
    Number(cant) > Number(stk)
  );
}

/**
 * Fila de vista previa: apta solo si quedó validada **OK** (línea verde). Se excluyen `error` y
 * cualquier `warning` (ítem SAP no encontrado, precio distinto, stock insuficiente, bodega faltante, etc.),
 * de modo que el Excel para reimportar contenga únicamente líneas listas para enviar a SAP.
 * Las verificaciones de precio/stock se mantienen como red de seguridad adicional.
 *
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function bulkPreviewRowEligibleForCleanPriceStockExport(row) {
  if (row == null || typeof row !== 'object') return false;
  if (row.validationStatus !== 'ok') return false;
  if (row.sapPriceMismatch) return false;
  if (bulkPreviewRowHasStockShortage(row)) return false;
  return true;
}

/**
 * Fila cuyo **único** problema es el stock (por demanda total): sin error, sin precio distinto y con
 * stock de bodega SAP conocido. Son las que el Excel para reimportar reparte en vez de excluir.
 *
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function bulkPreviewRowIsStockOnlyShortage(row) {
  if (row == null || typeof row !== 'object') return false;
  if (row.validationStatus === 'error') return false;
  if (row.sapPriceMismatch) return false;
  if (row.stockInsufficient !== true) return false;
  const stk = Number(row.stockDispSap);
  const cant = Number(row.cantOc);
  if (row.stockDispSap == null || !Number.isFinite(stk)) return false;
  if (!Number.isFinite(cant) || cant <= 0) return false;
  return true;
}

/** Clave de SKU para el reparto (ItemCode SAP resuelto si existe). */
function allocationSkuKey(row) {
  return String(row?.itemCodeSap || row?.skuIac || '')
    .trim()
    .toUpperCase();
}

/**
 * Reparte el stock disponible de cada SKU con faltante **en el orden del archivo**: la primera línea toma
 * lo que pide (o lo que quede), la siguiente el saldo, etc. Solo participan las filas cuyo único problema es
 * el stock (`bulkPreviewRowIsStockOnlyShortage`); las demás no consumen saldo.
 *
 * Nota: si el total de un SKU supera el stock, todas sus líneas quedan marcadas (ninguna queda `ok`),
 * así que el saldo inicial es el disponible SAP completo.
 *
 * @param {object[]} previewRows
 * @returns {{
 *   allocations: Map<number, { requested: number, allocated: number }>,
 *   summary: { skuCount: number, reducedLines: number, excludedLines: number, unitsRemoved: number }
 * }}
 */
export function allocateStockInFileOrder(previewRows) {
  const allocations = new Map();
  const summary = { skuCount: 0, reducedLines: 0, excludedLines: 0, unitsRemoved: 0 };
  if (!Array.isArray(previewRows)) return { allocations, summary };

  const remainingBySku = new Map();
  const touchedSkus = new Set();
  previewRows.forEach((row, idx) => {
    if (!bulkPreviewRowIsStockOnlyShortage(row)) return;
    const sku = allocationSkuKey(row);
    if (!sku) return;
    if (!remainingBySku.has(sku)) {
      remainingBySku.set(sku, Math.max(0, Math.floor(Number(row.stockDispSap))));
    }
    const requested = Number(row.cantOc);
    const left = remainingBySku.get(sku);
    const allocated = Math.max(0, Math.min(requested, left));
    remainingBySku.set(sku, left - allocated);
    allocations.set(idx, { requested, allocated });
    if (allocated < requested) {
      touchedSkus.add(sku);
      summary.unitsRemoved += requested - allocated;
      if (allocated === 0) summary.excludedLines += 1;
      else summary.reducedLines += 1;
    }
  });
  summary.skuCount = touchedSkus.size;
  return { allocations, summary };
}

/**
 * Por índice de fila en `grid.rows`, el índice correspondiente en `previewRows` (misma alineación que
 * `mapGridRowIndexToBulkValidation`: se saltan filas vacías).
 *
 * @param {{ rows?: any[][] }} grid
 * @param {number} headerIndex
 * @returns {Map<number, number>}
 */
export function mapGridRowIndexToPreviewIndex(grid, headerIndex) {
  const rows = grid?.rows || [];
  const map = new Map();
  const hi = Number(headerIndex);
  if (!Number.isFinite(hi) || hi < 0) return map;
  let pi = 0;
  for (let ri = hi + 1; ri < rows.length; ri += 1) {
    const r = rows[ri];
    if (!Array.isArray(r) || !r.some((c) => String(c ?? '').trim())) continue;
    map.set(ri, pi);
    pi += 1;
  }
  return map;
}

/**
 * Por índice de fila en `grid.rows`, indica si incluir la línea de detalle en el Excel «solo líneas OK» (reimportar).
 * Misma alineación que `mapGridRowIndexToBulkValidation`.
 *
 * @param {{ rows?: any[][] }} grid
 * @param {number} headerIndex
 * @param {object[]} previewRows
 * @returns {Map<number, boolean>}
 */
export function mapGridRowIndexToBulkCleanPriceStockExportEligible(grid, headerIndex, previewRows) {
  const map = new Map();
  if (!Array.isArray(previewRows)) return map;
  for (const [ri, pi] of mapGridRowIndexToPreviewIndex(grid, headerIndex).entries()) {
    const pr = previewRows[pi];
    map.set(ri, pr == null ? true : bulkPreviewRowEligibleForCleanPriceStockExport(pr));
  }
  return map;
}
